import { NavLink, useNavigate, useParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import { useCharacters, useCreateCharacter } from '../api/hooks/useCharacters';
import { useLocations, useCreateLocation } from '../api/hooks/useLocations';
import { useProps, useCreateProp } from '../api/hooks/useProps';
import { useCostumes, useCreateCostume } from '../api/hooks/useCostumes';
import { useScenes, useCreateScene } from '../api/hooks/useScenes';
import { useShots } from '../api/hooks/useShots';
import styles from './LeftRail.module.css';

const ITEM_TRANSITION = { duration: 0.15, ease: [0.2, 0, 0, 1] as const };
const ITEM_INITIAL = { opacity: 0, scale: 0.98 } as const;
const ITEM_ANIMATE = { opacity: 1, scale: 1 } as const;
const ITEM_EXIT = { opacity: 0 } as const;

export interface LeftRailProps {
  /** Active project id. Must be a positive number; hooks short-circuit on 0. */
  projectId: number;
}

interface NamedItem {
  id: number;
  name: string;
}

interface SimpleSectionProps {
  label: string;
  items: ReadonlyArray<NamedItem> | undefined;
  buildHref: (id: number) => string;
  onAdd?: () => void;
  addLabel: string;
  addPending?: boolean;
}

/**
 * One of the four leaf sections (characters / locations / props / costumes).
 * Renders an h3 label with a count badge, a + button, and a list of NavLinks.
 */
function SimpleSection({
  label,
  items,
  buildHref,
  onAdd,
  addLabel,
  addPending,
}: SimpleSectionProps) {
  const list = items ?? [];
  return (
    <section className={styles.section}>
      <h3 className={styles.label}>
        <span className={styles.labelText}>{label}</span>
        <span className={styles.count} aria-hidden="true">
          {list.length}
        </span>
        {onAdd ? (
          <button
            type="button"
            className={styles.addBtn}
            aria-label={addLabel}
            onClick={onAdd}
            disabled={addPending}
          >
            +
          </button>
        ) : null}
      </h3>
      <ul className={styles.list}>
        <AnimatePresence initial={false}>
          {list.map((item) => (
            <motion.li
              key={item.id}
              initial={ITEM_INITIAL}
              animate={ITEM_ANIMATE}
              exit={ITEM_EXIT}
              transition={ITEM_TRANSITION}
            >
              <NavLink
                to={buildHref(item.id)}
                className={({ isActive }) =>
                  isActive ? `${styles.item} ${styles.itemActive}` : styles.item
                }
              >
                <span className={styles.itemText}>{item.name}</span>
              </NavLink>
            </motion.li>
          ))}
        </AnimatePresence>
      </ul>
    </section>
  );
}

/**
 * Find the smallest unique name with form `${base}` or `${base} N` (N >= 2)
 * that does NOT collide with any item in `existing` (case-insensitive).
 *
 * Used only for the default placeholder names of newly-created entities;
 * the user immediately renames via the inline-rename pattern.
 */
function uniqueDefaultName(base: string, existing: ReadonlyArray<{ name: string }>): string {
  const taken = new Set(existing.map((e) => e.name.toLowerCase()));
  if (!taken.has(base.toLowerCase())) return base;
  for (let n = 2; n < 1000; n++) {
    const candidate = `${base} ${n}`;
    if (!taken.has(candidate.toLowerCase())) return candidate;
  }
  // Pathological: 1000 collisions. Fall through with a stamp.
  return `${base} ${Date.now()}`;
}

/**
 * Left navigation rail (240px, collapses to 56px below 1100px).
 *
 * Renders five sections for the active project: characters, locations, props,
 * costumes, and scenes. When the URL matches a scene, that scene's <li>
 * expands inline to show the scene's shots underneath. The active link is
 * automatically marked aria-current="page" by react-router's NavLink.
 *
 * Each section header carries a `+` button that creates a new entity with
 * a placeholder default name and immediately navigates to its editor with
 * `?focus=name` so the user can rename in place.
 */
export function LeftRail({ projectId }: LeftRailProps) {
  const { sceneId } = useParams<{ sceneId?: string }>();
  const activeSceneIdNum = sceneId ? Number(sceneId) : 0;
  const navigate = useNavigate();

  const characters = useCharacters(projectId);
  const locations = useLocations(projectId);
  const props = useProps(projectId);
  const costumes = useCostumes(projectId);
  const scenes = useScenes(projectId);
  const activeShots = useShots(activeSceneIdNum);

  const createScene = useCreateScene(projectId);
  const createCharacter = useCreateCharacter(projectId);
  const createLocation = useCreateLocation(projectId);
  const createProp = useCreateProp(projectId);
  const createCostume = useCreateCostume(projectId);

  const sceneList = scenes.data ?? [];
  const shotList = activeShots.data ?? [];

  const onAddScene = () => {
    if (createScene.isPending) return;
    const ordinal = sceneList.length;
    const name = `Scene ${ordinal + 1}`;
    createScene.mutate(
      { ordinal, name },
      {
        onSuccess: (created) => {
          navigate(`/projects/${projectId}/scenes/${created.id}?focus=name`);
        },
      },
    );
  };

  const onAddCharacter = () => {
    if (createCharacter.isPending) return;
    const name = uniqueDefaultName('Untitled character', characters.data ?? []);
    createCharacter.mutate(
      { name, base_description: name, status: 'draft' },
      {
        onSuccess: (created) => {
          navigate(`/projects/${projectId}/characters/${created.id}?focus=name`);
        },
      },
    );
  };

  const onAddLocation = () => {
    if (createLocation.isPending) return;
    const name = uniqueDefaultName('Untitled location', locations.data ?? []);
    createLocation.mutate(
      { name, description: name },
      {
        onSuccess: (created) => {
          navigate(`/projects/${projectId}/locations/${created.id}?focus=name`);
        },
      },
    );
  };

  const onAddProp = () => {
    if (createProp.isPending) return;
    const name = uniqueDefaultName('Untitled prop', props.data ?? []);
    createProp.mutate(
      { name, description: name },
      {
        onSuccess: (created) => {
          navigate(`/projects/${projectId}/props/${created.id}?focus=name`);
        },
      },
    );
  };

  const onAddCostume = () => {
    if (createCostume.isPending) return;
    const name = uniqueDefaultName('Untitled costume', costumes.data ?? []);
    createCostume.mutate(
      { name, description: name },
      {
        onSuccess: (created) => {
          navigate(`/projects/${projectId}/costumes/${created.id}?focus=name`);
        },
      },
    );
  };

  return (
    <nav className={styles.rail} aria-label="Project navigation">
      <SimpleSection
        label="Characters"
        items={characters.data}
        buildHref={(id) => `/projects/${projectId}/characters/${id}`}
        onAdd={onAddCharacter}
        addLabel="Add character"
        addPending={createCharacter.isPending}
      />
      <SimpleSection
        label="Locations"
        items={locations.data}
        buildHref={(id) => `/projects/${projectId}/locations/${id}`}
        onAdd={onAddLocation}
        addLabel="Add location"
        addPending={createLocation.isPending}
      />
      <SimpleSection
        label="Props"
        items={props.data}
        buildHref={(id) => `/projects/${projectId}/props/${id}`}
        onAdd={onAddProp}
        addLabel="Add prop"
        addPending={createProp.isPending}
      />
      <SimpleSection
        label="Costumes"
        items={costumes.data}
        buildHref={(id) => `/projects/${projectId}/costumes/${id}`}
        onAdd={onAddCostume}
        addLabel="Add costume"
        addPending={createCostume.isPending}
      />

      <section className={styles.section}>
        <h3 className={styles.label}>
          <span className={styles.labelText}>Scenes</span>
          <span className={styles.count} aria-hidden="true">
            {sceneList.length}
          </span>
          <button
            type="button"
            className={styles.addBtn}
            aria-label="Add scene"
            onClick={onAddScene}
            disabled={createScene.isPending}
          >
            +
          </button>
        </h3>
        <ul className={styles.list}>
          <AnimatePresence initial={false}>
            {sceneList.map((scene) => {
              const isActiveScene = scene.id === activeSceneIdNum;
              return (
                <motion.li
                  key={scene.id}
                  initial={ITEM_INITIAL}
                  animate={ITEM_ANIMATE}
                  exit={ITEM_EXIT}
                  transition={ITEM_TRANSITION}
                >
                  <NavLink
                    to={`/projects/${projectId}/scenes/${scene.id}`}
                    className={({ isActive }) =>
                      isActive ? `${styles.item} ${styles.itemActive}` : styles.item
                    }
                    end
                  >
                    <span className={styles.itemText}>{scene.name}</span>
                  </NavLink>
                  {isActiveScene && shotList.length > 0 && (
                    <ul className={styles.shotList}>
                      {shotList.map((shot) => (
                        <li key={shot.id}>
                          <NavLink
                            to={`/projects/${projectId}/scenes/${scene.id}?shot=${shot.id}`}
                            className={styles.shotLink}
                          >
                            <span className={styles.shotNum}>{shot.shot_number}</span>
                            <span className={styles.shotText}>{shot.raw_description}</span>
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  )}
                </motion.li>
              );
            })}
          </AnimatePresence>
        </ul>
      </section>
    </nav>
  );
}
